/**
 * Opens an external link or document via the native mobile container bridge.
 *
 * Royal Momentum is shipped inside a mobile WebView container that exposes
 * `vizconContainer.nativeCall` for routing URLs out of the WebView:
 *   - openInBrowser → opens the URL in the system browser / in-app browser
 *   - openDocument  → opens a document (PDF, image, etc.) in the native viewer
 *
 * Falls back to `window.open` when the bridge isn't available (e.g. desktop
 * dev mode), so the same code works in both contexts.
 *
 * @param {Object}            options
 * @param {boolean}           [options.isExternal=false] - When true, always uses openInBrowser regardless of type.
 * @param {"document"|"link"} [options.type="document"]  - "link" → opens in browser; "document" → opens in document viewer.
 * @param {string}            options.url                - URL to open.
 */
/* global vizconContainer */
export const openOutsideLink = ({ isExternal = false, type = "document", url }) => {
    let finalUrlForLink = url;

    try {
        if (isExternal) {
            vizconContainer.nativeCall({
                method: "openInBrowser",
                data: url,
            });
        } else {
            if (type === "link") {
                vizconContainer.nativeCall({
                    method: "openInBrowser",
                    data: finalUrlForLink,
                });
            } else {
                vizconContainer.nativeCall({
                    method: "openDocument",
                    data: { url, absolute: false },
                });
            }
        }
    } catch (error) {
        window.open(finalUrlForLink, "_blank");
    }
};

/**
 * Opens the device's mail composer through the container bridge.
 *
 * The packaged build cannot use a `mailto:` URL: assigning `window.location.href` asks the WebView
 * to navigate *itself* to a scheme it does not handle, which does nothing at all. `sendMail` is the
 * container's own route to the OS mail client.
 *
 * `recipient` and `attachment` are deliberately left empty — the rep chooses who to send to in
 * their mail client, and there is no file to attach; the notes travel in the body. The three
 * trailing fields are part of the bridge's `sendMail` contract and are sent as the container
 * expects even though no attachment is involved.
 *
 * @param {Object} options
 * @param {string} [options.subject] - Mail subject line.
 * @param {string} [options.body]    - Mail body, plain text.
 * @returns {boolean} False when the bridge is absent or rejected the call, so the caller can fall
 * back to `mailto:` rather than appearing to succeed.
 */
export const sendNativeMail = ({ subject = "", body = "" } = {}) => {
    try {
        vizconContainer.nativeCall({
            method: "sendMail",
            data: {
                recipient: "",
                subject,
                body,
                attachment: "",
                sendLargeFilesAsLink: false,
                maxFileSizeInKiloBytes: 100000,
                basePath: "",
            },
        });
        return true;
    } catch (error) {
        return false;
    }
};
